<template>
  <div class="billing-payment-credit">
    <div v-if="payment && isCvvReAuthRequired" class="billing-payment-credit__mask-number-row">
      <img :src="creditIcon" />
      <span class="billing-payment-credit__mask-number-text">{{ payment.info }}</span>
      <span class="billing-payment-credit__mask-number-label">{{ payment.label }}</span>
    </div>
    <div v-loading="formLoading">
      <div class="billing-payment-credit__wrap" :class="{ 'billing-payment-credit__loaded': !formLoading }">
        <div :id="zuoraContainerId"></div>
      </div>
      <zm-alert v-if="paymentError" type="error" show-icon :closable="false" class="billing-payment-credit__error">
        <p class="billing-payment-credit__error--desc" v-html="paymentError" />
      </zm-alert>
      <template v-if="!isCvvReAuthRequired">
        <zm-checkbox
          v-if="hasValidDefaultAddress"
          :value="isSameAsContactSign"
          @change="setIsSameContactSign"
          :disabled="disabledBillCardForm"
          :aria-label="__$t('billing.payment.bill_same_as_contact')"
          class="billing-payment-credit__same-checkbox"
        >
          {{ __$t('billing.payment.bill_same_as_contact') }}
          <zm-tooltip v-show="rpsNoEdit" :content="__$t('billing.rps_no_update')" placement="bottom">
            <i class="zm-icon-help-outline" />
          </zm-tooltip>
        </zm-checkbox>
        <template>
          <bill-address-detail v-show="isSameAsContactSign" :default-address="defaultAddress" />
          <bill-address-form
            :form="billAddressFormModel"
            v-show="!isSameAsContactSign"
            :disabled="disabledBillCardForm"
            ref="billToContactInfo"
          />
        </template>
        <slot name="payment-method-error"></slot>
      </template>
    </div>
  </div>
</template>
<script>
import { trim } from 'lodash'
import { mapGetters } from 'vuex'
import { ERROR_MAPPING, Z_HPM_IFRAME, payMethodMap } from '../../common/constant'
import { APP_NAMESPACE } from '../../store'
import Ready from '../../common/ready'
import { countryAlphaMapping } from '../../common/regionUtils'
import BillAddressDetail from './bill-address-detail'
import BillAddressForm from './bill-address-form'
import PaymentBase from './common/PaymentBase'
import { getDefaultAddressInfoForCredit } from '../../store/helper'
import { logger } from '../../utils/log'
import { getEffectiveCurrency } from '../../utils/currency'
import { translateMixins } from '../../plugins/i18n'
import { getCreditCardIcon } from '../../utils/payment'
import zuoraMixin from './mixins/zuora-mixin'

const Zuora = window.Z

export default {
  name: 'Credit',
  extends: PaymentBase,
  mixins: [translateMixins, zuoraMixin],
  inject: ['payment'],
  components: { BillAddressForm, BillAddressDetail },
  props: {
    isFormVisible: {
      type: Boolean,
      default: false
    },
    indiaMandateAmount: String
  },
  data() {
    return {
      validateInstance: new Ready(),
      billAddressFormModel: getDefaultAddressInfoForCredit(),
      isDesktop: window._billing_responsive.isDesktop
    }
  },
  created() {
    this.resetBillAddressFormModel()
  },
  mounted() {
    if (this.isFormVisible) {
      this.initCreditCardForm()
    }
  },
  beforeDestroy() {
    logger.log('HPM before destroy.........................................')
    document.getElementById(Z_HPM_IFRAME)?.removeEventListener('load', this.zuoraOnLoadEventListener)
    this.resetFormState()
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, [
      'billToAddress',
      'billingInfo',
      'selectedPaymentMethod',
      'billingInfoConfig',
      'countryMap'
    ]),
    paymentAddress() {
      if (!this.payment || !this.payment.paymentData) return null
      const {
        creditCardCity,
        creditCardCountry,
        creditCardState,
        creditCardAddress1,
        creditCardAddress2,
        creditCardPostalCode
      } = this.payment.paymentData || {}
      const address = {
        address1: creditCardAddress1,
        address2: creditCardAddress2,
        country: this.countryMap[creditCardCountry] || creditCardCountry,
        countryName: creditCardCountry,
        state: creditCardState,
        city: creditCardCity,
        zip: creditCardPostalCode
      }
      if (this.isValidAddress(address)) {
        return address
      }
      return null
    },
    defaultCardHolderName() {
      const { firstName = '', lastName = '' } = this.account

      return trim([firstName, lastName].join(' '))
    },
    defaultAddress() {
      if (this.paymentAddress) return this.paymentAddress

      if (this.billToAddress && this.isValidAddress(this.billToAddress)) return this.billToAddress

      return getDefaultAddressInfoForCredit()
    },
    prepopulateFields() {
      const creditCardHolderName = this.defaultCardHolderName || ''
      return {
        creditCardHolderName: creditCardHolderName?.length > 50 ? '' : creditCardHolderName,
        creditCardAddress1: this.defaultAddress.address1 ?? '',
        creditCardAddress2: this.defaultAddress.address2 ?? '',
        creditCardCity: this.defaultAddress.city ?? '',
        // creditCardState: this.defaultAddress.state ?? '',
        creditCardPostalCode: this.defaultAddress.zip ?? '',
        // creditCardCountry: countryAlphaMapping[this.defaultAddress.country],
        email: this.account.email ?? ''
      }
    },
    billToContactInfoForm() {
      return this.$refs?.billToContactInfo
    },
    errorMapping() {
      return Object.assign({}, ERROR_MAPPING['DEFAULT'], ERROR_MAPPING[getEffectiveCurrency()], ERROR_MAPPING['Adyen'])
    },
    countryCode() {
      const { isSameAsContactSign, country: billFormCountry } = this.billAddressFormModel
      const billToCountry = !isSameAsContactSign && billFormCountry ? billFormCountry : this.defaultAddress?.country

      if (billToCountry) {
        return billToCountry
      } else {
        return 'DEFAULT'
      }
    },
    disabledBillCardForm() {
      // 1. zuora hpm is loading
      // 2. zuora hpm have system error
      // 3. rps not edit
      // 4. payment method is submitting
      return this.formLoading || !this.zuoraHpmIsValid || this.rpsNoEdit
    },
    rpsNoEdit() {
      const isE2OpenRPSPending = this.billingInfoConfig.e2open.e2OpenRPSStatus === 'Pending Review'
      return this.billingInfoConfig.e2open.e2OpenEnabled && isE2OpenRPSPending
    },
    creditIcon() {
      return getCreditCardIcon(this.payment?.subType)
    },
    isSameAsContactSign() {
      if (this.hasValidDefaultAddress) {
        return this.billAddressFormModel.isSameAsContactSign
      }
      return false
    },
    hasValidDefaultAddress() {
      return this.isValidAddress(this.defaultAddress)
    }
  },
  methods: {
    isValidAddress(address) {
      return address.city && address.address1 && address.state && address.country && address.zip
    },
    initCreditCardForm() {
      this.loadHpmIframe(payMethodMap.credit)
      this.listenToZuoraMessage()
    },
    resetBillAddressFormModel() {
      this.billAddressFormModel = getDefaultAddressInfoForCredit()
    },
    setIsSameContactSign(data) {
      this.billAddressFormModel.isSameAsContactSign = data
      if (data) {
        this.resetBillAddressFormModel()
      }
    },
    setHpmHiddenFieldValues() {
      const contactInfo = this.isSameAsContactSign ? this.defaultAddress : this.billAddressFormModel

      Zuora.setFieldValue('creditCardAddress1', contactInfo.address1 ?? '')
      Zuora.setFieldValue('creditCardAddress2', contactInfo.address2 ?? '')
      contactInfo.city && Zuora.setFieldValue('creditCardCity', contactInfo.city ?? '')
      contactInfo.state && Zuora.setFieldValue('creditCardState', contactInfo.state ?? '')
      Zuora.setFieldValue('creditCardPostalCode', contactInfo.zip ?? '')
      Zuora.setFieldValue('creditCardCountry', countryAlphaMapping[contactInfo.country ?? 'US'])
      Zuora.setFieldValue('email', this.account.email ?? '')
    },
    async validBillToContactInfoForm() {
      if (this.isSameAsContactSign) {
        return Promise.resolve()
      } else {
        return this.billToContactInfoForm?.validate()
      }
    },
    async $submit() {
      if (!this.isCvvReAuth) {
        // validate billing contact form
        await this.validBillToContactInfoForm()
      }
      // validate zuora hpm form
      await this.validateHpmForm(payMethodMap.credit)
      // submit zuora hpm form
      const paymentInfo = await this.submitHpmForm(payMethodMap.credit)
      // if the with cc account, need call update payment api
      await this.updatePayMethodAndContactForm(paymentInfo)
      return paymentInfo
    }
  },
  watch: {
    isFormVisible: {
      handler(isFormVisible) {
        this.resetFormState()
        if (isFormVisible) {
          this.clearPaymentError()
          this.initCreditCardForm()
        } else {
          document.getElementById(Z_HPM_IFRAME)?.removeEventListener('load', this.zuoraOnLoadEventListener)
          this.resetFormState()
          document.getElementById(Z_HPM_IFRAME)?.remove()
          clearInterval(this.resizeHpmOnIntervalRef)
          logger.log('HPM unloaded.........................................')
        }
      }
    },
    authPaymentGateWay: {
      handler() {
        this.reRenderPayMethod()
      }
    },
    formLoading: {
      handler(_, newFormLoading) {
        if (newFormLoading) {
          this.ready()
        }
      }
    }
  },
  destroyed() {
    logger.log('credit card has destroyed')
  }
}
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.billing-payment-credit {
  &__mask-number-row {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    img {
      width: 60px;
      height: 40px;
      object-fit: contain;
      font-size: 0;
      margin-right: 16px;
    }
  }
  &__mask-number-text {
    color: #131619;
    /* Label/Label M */
    font-family: 'Almaden Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%; /* 21px */
    margin-right: 4px;
  }
  &__mask-number-label {
    color: #6e7680;
    /* Body/Body S */
    font-family: 'Almaden Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 21px */
  }
  &__wrap {
    margin-top: 16px;
    margin-bottom: 24px;
    padding: 24px 6px 16px;
    border-radius: 10px;
    overflow: hidden;
  }

  &__loaded {
    background: #f2f2f4;
    border: 1px solid #dfdfe4;
  }

  #zuora_payment {
    width: 528px;
    height: auto;
    min-height: 150px;
    margin: auto;
    @include media(mobile, tablet) {
      width: 100%;
      padding: 0 6px;
    }
  }

  &__same-checkbox {
    .zm-checkbox__wrap {
      padding: 0;
    }
  }

  &__hidden-addr {
    display: none;
  }

  &__error.zm-alert {
    background-color: #f3c2d0;
    color: #80384d;
    margin-bottom: 24px;

    &--desc {
      word-break: break-word;
    }
  }

  #z_hppm_iframe {
    background: transparent;
    width: 100%;
  }

  &__action {
    display: flex;
    margin-top: 24px;

    &.justify-end {
      justify-content: flex-end;
    }

    .zm-button {
      font-weight: 600 !important;
    }

    &__cancel {
      &.zm-button {
        margin-left: 16px;
      }
    }
  }
}
</style>
